import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  CssBaseline,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useLaunchStore } from "../services/store/useLaunchStore";
import Header from "./Header_new";
import UserSidebar from "./UserSidebar_upated";
import { onMessage } from "firebase/messaging";
import { messaging } from "../utils/firebase";
import { enqueueSnackbar } from "notistack";
import { useAuth, useCurrentTalentProfile, useSidebarStore } from "../hooks";
import SocketHandler from "../utils/socketHandler";
import {
  getTalentSocketNotification,
  getUserSocketNotification,
} from "../utils/notificationHandler";
import { SocketNotificationData } from "../components/socketNotification/types";
import { playNotificationSound } from "../utils/notificationSound";
import { useNotificationStore } from "../services/store/useNotificationStore";
import { SocketNotificationType } from "../constants/appConstants";
import { useTranslation } from "react-i18next";

const UserLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const {
    currentNotification,
    setCurrentNotification,
    clearCurrentNotification,
  } = useNotificationStore();

  const { userInfo } = useAuth();

  const { currentProfile } = useCurrentTalentProfile();

  const toggleDrawer = () => setDrawerOpen((prev) => !prev);
  const location = useLocation();
  const navigate = useNavigate();
  const [badgeVisible, setBadgeVisible] = useState(false);

  const [socketNotification, setSocketNotification] = useState<any>([]);
  const [coffeeJoinChannel, setCoffeeJoinChannel] = useState<string | null>(
    null,
  );

  const socketSubscribedRef = useRef(false);
  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current || !userInfo?.id) {
      return;
    }

    try {
      // Get socket instance and subscribe to messages
      const socketHandler = SocketHandler.getInstance();

      socketHandler.subscribeToNotification(
        userInfo.id.toString(),
        (message) => {
          if (message) {
            const notification = currentProfile?.id
              ? getTalentSocketNotification(message, currentProfile?.id)
              : getUserSocketNotification(message);
            if (notification) {
              setSocketNotification((prev: any) => [...prev, notification]);
              setBadgeVisible(true);
              setCurrentNotification(notification);
              // Play notification sound
              playNotificationSound();
              console.log("notification are here in user layout", notification);
            }
          }

          // Handle the message here
        },
      );

      socketSubscribedRef.current = true;
      console.log("Successfully subscribed to notification socket");
    } catch (error) {
      console.error("Failed to subscribe to socket:", error);
    }

    // Cleanup subscription when component unmounts
    return () => {
      try {
        if (userInfo?.id) {
          const socketHandler = SocketHandler.getInstance();
          socketHandler.unsubscribeFromNotification(userInfo.id.toString());
          socketSubscribedRef.current = false;
          console.log("Successfully unsubscribed from notification socket");
        }
      } catch (error) {
        console.error("Failed to unsubscribe from socket:", error);
      }
    };
  }, [userInfo?.id, currentProfile?.id]);

  // coffee call
  const setCoffeeCallNotification = useSidebarStore(
    (state) => state.setCoffeeCallNotification,
  );
  const coffeeCallNotification = useSidebarStore(
    (state) => state.coffeeCallNotification,
  );

  //regular call notification
  const setRegularCallNotification = useSidebarStore(
    (state) => state.setRegularCallNotification,
  );
  const regularCallNotification = useSidebarStore(
    (state) => state.regularCallNotification,
  );

  // group call invitation
  const setGroupCallInvitation = useSidebarStore(
    (state) => state.setGroupCallInvitation,
  );
  const groupCallInvitation = useSidebarStore(
    (state) => state.groupCallInvitation,
  );

  // group call notification
  const setGroupCallNotification = useSidebarStore(
    (state) => state.setGroupCallNotification,
  );
  const groupCallNotification = useSidebarStore(
    (state) => state.groupCallNotification,
  );

  //  call confirmation
  const setCallConfirmation = useSidebarStore(
    (state) => state.setCallConfirmation,
  );

  //  call remainder
  const setCallRemainder = useSidebarStore((state) => state.setCallRemainder);

  const regularCallNotificationFromLocal = JSON.parse(
    localStorage.getItem("regularCallNotification") || "null",
  );
  const groupCallNotificationFromLocal = JSON.parse(
    localStorage.getItem("groupCallNotification") || "null",
  );
  const coffeeCallNotificationfromLocal = JSON.parse(
    localStorage.getItem("coffeeCallNotification") || "null",
  );
  const groupCallInvitationfromLocal = JSON.parse(
    localStorage.getItem("groupCallInvitation") || "null",
  );

  useEffect(() => {
    if (socketNotification.length > 0) {
      socketNotification.forEach((notification: SocketNotificationData) => {
        if (notification.notification_type === "coffee-call-started") {
          enqueueSnackbar("Incoming coffee call!", { variant: "info" });
          setCoffeeJoinChannel(notification?.channel_name || null);
          setCoffeeCallNotification(notification);
          localStorage.setItem(
            "coffeeCallNotification",
            JSON.stringify(notification),
          );
        } else if (notification.notification_type === "coffee-call-ended") {
          setCoffeeJoinChannel(null);
        } else if (
          notification.notification_type ===
          SocketNotificationType.BookingStarted
        ) {
          if (notification.is_group_session) {
            enqueueSnackbar("Incoming group call!", { variant: "info" });
            setGroupCallNotification(notification);
            localStorage.setItem(
              "groupCallNotification",
              JSON.stringify(notification),
            );
          } else {
            enqueueSnackbar("Incoming call!", { variant: "info" });
            setRegularCallNotification(notification);
            localStorage.setItem(
              "regularCallNotification",
              JSON.stringify(notification),
            );
          }
        } else {
          setCoffeeJoinChannel(null);
        }
      });
    }
  }, [socketNotification]);

  useEffect(() => {
    if (
      currentNotification?.notification_type ===
      SocketNotificationType.FlashCardBookingRefund
    ) {
      enqueueSnackbar(
        t("flash_card_refund_notification", {
          title: currentNotification?.title,
          price: currentNotification?.price,
        }),
        { variant: "success" },
      );
      clearCurrentNotification();
    }

    if (
      currentNotification?.notification_type ===
      SocketNotificationType.FlashCardStarted
    ) {
      enqueueSnackbar(t("the_flash_card_you_bought_has_started"), {
        variant: "success",
      });
      clearCurrentNotification();
    }
  }, [
    currentNotification?.title,
    currentNotification?.notification_type,
    clearCurrentNotification,
  ]);

  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("payload", payload);

      if (
        (payload.data?.type === "group_session" ||
          payload.data?.type === "regular_session") &&
        payload?.notification?.title?.includes("Healthy Reminder")
      ) {
        enqueueSnackbar(payload?.notification?.body, { variant: "info" });
        setCallRemainder(payload);
        localStorage.setItem("callRemainder", JSON.stringify(payload));
      }

      // if (
      //   payload.data?.type === "group_session" &&
      //   !payload?.notification?.title?.includes("Healthy Reminder")
      // ) {
      //   enqueueSnackbar("Incoming group call!", { variant: "info" });
      //   setGroupCallNotification(payload);
      //   localStorage.setItem("groupCallNotification", JSON.stringify(payload));
      // }

      if (payload.data?.type === "booking_accepted") {
        enqueueSnackbar("Your booking has been accepted!", { variant: "info" });
        setCallConfirmation(payload);
        localStorage.setItem("callConfirmation", JSON.stringify(payload));
      }

      // if (payload.data?.type === "booking_invite") {
      //   enqueueSnackbar("You have an invitation!", { variant: "info" });
      //   setGroupCallInvitation(payload);
      //   localStorage.setItem("groupCallInvitation", JSON.stringify(payload));
      // }
    });

    return () => unsubscribe();
  }, []);

  const CreateCard = () => navigate("/create-card");

  const pathSegments = location.pathname.split("/").filter(Boolean);

  const handleBreadcrumbClick = (index: number) => {
    const path = "/" + pathSegments.slice(0, index + 1).join("/");
    navigate(path);
  };

  const handleClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* Overlay lives here — above the <Outlet> — so it persists across route changes */}
      <GlobalLaunchOverlay />
      <Header
        setBadgeVisible={setBadgeVisible}
        badgeVisible={badgeVisible}
        socketNotification={socketNotification}
        toggleDrawer={toggleDrawer}
        onClickCreate={CreateCard}
        coffeeCallNotificationfromLayout={
          coffeeCallNotification || coffeeCallNotificationfromLocal
        }
        regularCallNotificationfromLayout={
          regularCallNotification || regularCallNotificationFromLocal
        }
        groupCallNotificationfromLayout={
          groupCallNotification || groupCallNotificationFromLocal
        }
        groupCallInvitationfromLayout={
          groupCallInvitation || groupCallInvitationfromLocal
        }
      />
      <UserSidebar
        handleClose={handleClose}
        open={drawerOpen}
        toggleDrawer={toggleDrawer}
        coffeeJoinChannel={coffeeJoinChannel}
        // coffeeCallNotification={
        //   coffeeCallNotification || coffeeCallNotificationfromLocal
        // }
        // coffeeCallChannel={
        //   coffeeCallNotification?.data?.channel_name ||
        //   COFFEE_CALL_DUMMY_CHANNEL
        // }
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          px: { xs: 1, md: 3 },
          py: 3,
          overflow: "hidden",
        }}
      >
        <Toolbar />
        {/* <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/")}
            sx={{ cursor: "pointer" }}
          ></Link>
          {pathSegments.map((seg, index) => {
            const isLast = index === pathSegments.length - 1;
            const label =
              seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " ");
            return isLast ? (
              <Typography color="text.primary" key={index}>
                {label}
              </Typography>
            ) : (
              <Link
                key={index}
                underline="hover"
                color="inherit"
                sx={{ cursor: "pointer" }}
                onClick={() => handleBreadcrumbClick(index)}
              >
                {label}
              </Link>
            );
          })}
        </Breadcrumbs> */}
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

// ── Global launch overlay — rendered ABOVE the Outlet so it survives navigation ─
const GlobalLaunchOverlay: React.FC = () => {
  const { payload, clear } = useLaunchStore();
  const [expanded, setExpanded] = useState(false);
  const prevPayloadRef = useRef<typeof payload>(null);

  useEffect(() => {
    if (!payload) {
      setExpanded(false);
      return;
    }
    // Only kick off the expansion when a new payload arrives
    if (payload !== prevPayloadRef.current) {
      prevPayloadRef.current = payload;
      setExpanded(false);
      let id1: number, id2: number;
      id1 = requestAnimationFrame(() => {
        id2 = requestAnimationFrame(() => setExpanded(true));
      });
      // Auto-clear after animation + navigation settle
      const t = setTimeout(() => clear(), 500);
      return () => {
        cancelAnimationFrame(id1);
        cancelAnimationFrame(id2);
        clearTimeout(t);
      };
    }
  }, [payload, clear]);

  if (!payload) return null;

  const { bg, glowColor, iconColor, icon, originX, originY, startRadius } =
    payload;
  const endR = Math.hypot(window.innerWidth, window.innerHeight) + 50;
  const ease = "cubic-bezier(0.32, 0.72, 0, 1)";
  const dur = "0.46s";

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: bg,
        pointerEvents: "none",
        clipPath: expanded
          ? `circle(${endR}px at ${originX}px ${originY}px)`
          : `circle(${startRadius}px at ${originX}px ${originY}px)`,
        transition: expanded ? `clip-path ${dur} ${ease}` : "none",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(140deg, rgba(255,255,255,0.20) 0%, transparent 40%)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: originY,
          left: originX,
          transform: expanded
            ? "translate(-50%,-50%) scale(3.5)"
            : "translate(-50%,-50%) scale(1)",
          opacity: expanded ? 0 : 1,
          transition: expanded
            ? `transform ${dur} ${ease}, opacity 0.25s ease`
            : "none",
          color: iconColor,
          filter: `drop-shadow(0 0 20px ${glowColor})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
    </Box>
  );
};

export default UserLayout;
